import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Box1, Card, Shop, TickCircle, TruckFast } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { SefanoCheckbox } from "@/components/Atoms";
import { SefanoButton } from "@/components/Molecules";
import type { SettingsShippingProps } from "@/components/Templates/Settings/Settings.template.types";
import * as styles from "./SettingsShippingPanel.styles";

type SettingsShippingPanelProps = SettingsShippingProps;

const SHIPPING_ICONS = {
  post: Box1,
  courier: TruckFast,
  pickup: Shop,
};

export function SettingsShippingPanel({
  onSubmit,
  shippingOptions,
  smsOptions,
  methodsError,
  canSubmit,
  isLoading,
  isSubmitting,
  saved,
  saveError,
}: SettingsShippingPanelProps) {
  const { t } = useTranslation("settings");
  const theme = useTheme();

  if (isLoading) {
    return <CircularProgress size={32} />;
  }

  return (
    <Box component="form" onSubmit={onSubmit} sx={styles.stack}>
      <Typography sx={styles.sectionTitle}>{t("shipping.methodsTitle")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {shippingOptions.map((option) => {
          const Icon = SHIPPING_ICONS[option.key];
          return (
            <Box
              key={option.key}
              role="checkbox"
              aria-checked={option.checked}
              tabIndex={0}
              sx={styles.shipOption(option.checked)}
              onClick={option.onToggle}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  option.onToggle();
                }
              }}
            >
              <Icon size={22} variant="Bold" color={theme.palette.primary.main} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: { xs: "0.78125rem", md: "0.84375rem" }, fontWeight: 600 }}>
                  {t(option.title)}
                </Typography>
                <Typography sx={{ fontSize: { xs: "0.6875rem", md: "0.75rem" }, color: "text.secondary" }}>
                  {t(option.description)}
                </Typography>
              </Box>
              <SefanoCheckbox checked={option.checked} />
            </Box>
          );
        })}
        {methodsError ? (
          <Typography role="alert" sx={{ fontSize: "0.71875rem", fontWeight: 600, color: "error.main" }}>
            {methodsError}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          p: 1.75,
          borderRadius: 1.75,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Card size={22} variant="Bold" color={theme.palette.primary.main} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: { xs: "0.78125rem", md: "0.84375rem" }, fontWeight: 700 }}>
            {t("shipping.gatewayTitle")}
          </Typography>
          <Typography sx={{ fontSize: { xs: "0.6875rem", md: "0.75rem" }, color: "text.secondary", lineHeight: 1.8 }}>
            {t("shipping.gatewayDescription")}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.sectionDivider}>
        <Typography sx={styles.sectionTitle}>{t("shipping.smsTitle")}</Typography>
        {smsOptions.map((option) => (
          <Box key={option.key} sx={styles.smsRow}>
            <Box>
              <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.8125rem" } }}>{t(option.title)}</Typography>
              <Typography sx={{ fontSize: { xs: "0.65625rem", md: "0.71875rem" }, color: "text.secondary" }}>
                {t(option.description)}
              </Typography>
            </Box>
            <SefanoCheckbox checked={option.checked} onChange={option.onToggle} />
          </Box>
        ))}
      </Box>

      {saveError ? (
        <Typography role="alert" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "error.main" }}>
          {saveError}
        </Typography>
      ) : null}

      <Box sx={styles.footerRow}>
        <SefanoButton type="submit" disabled={!canSubmit} sx={{ width: { xs: "100%", md: 150 } }}>
          {isSubmitting ? t("saving") : t("shipping.save")}
        </SefanoButton>
        {saved ? (
          <Box component="span" sx={styles.savedFlash}>
            <TickCircle size={16} variant="Bold" color={theme.palette.success.main} />
            {t("saved")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
